import PropTypes from 'prop-types'
import { useLocation } from 'react-router-dom'
import LoadingScreen from '@/components/LoadingScreen'
import AcceptTerms from '@/features/agreements/AcceptTerms'
import { needsTermsAcceptance } from '@/features/agreements/termsPolicy'
import CreateOrganisation from '@/features/allauth/pages/account/CreateOrganisation'
import useAuth from '@/hooks/useAuth'
import { SITE_ADMIN_ROLES } from '@/lib/roles'
import TrialExpired from '@/pages/TrialExpired'
import { hasRole } from '@/utils/hasRole'

// Paths that must stay reachable even when the active org's trial has expired — most
// importantly the billing route itself. Withdrawing access must not lock the admin out of
// the one route that lets them fix it (design decision 11 / task 4.5's regression concern,
// mirrored here on the frontend side of the same guarantee).
const BILLING_ESCAPE_PATHS = ['/admin/billing']

/**
 * Routes a signed-in user to the right place instead of an empty or half-broken dashboard
 * (self-serve-org-signup tasks 7.4 / 7.8):
 *
 *   - no org yet (and not partner staff)         -> the organisation-creation step
 *   - org admin whose org hasn't accepted terms   -> the terms acceptance page
 *   - active org's trial has expired              -> the trial-expired page
 *   - otherwise                                    -> the normal dashboard
 *
 * Wraps DashboardLayout the same way AuthGuard does (returning a page in place of children
 * rather than redirecting), so every dashboard route is covered without an opt-in flag on
 * each one — unlike BillingGuard/`requiresBilling`, which is a per-route, fail-open opt-in
 * for a handful of premium pages and deliberately left alone by this change.
 */
const OrgAccessGuard = ({ children }) => {
    const { user, activeOrg, activeOrgLoaded, isAuthenticated } = useAuth()
    const location = useLocation()

    if (!isAuthenticated) {
        return <>{children}</>
    }

    // Wait for the org fetch that follows session init — otherwise a freshly-verified user
    // flashes into org creation for a frame before their real org has loaded.
    if (!activeOrgLoaded) {
        return <LoadingScreen />
    }

    // Site admins manage every org and must never be locked out of the app by one org's
    // billing state. Partner staff have no home org by design (org=null; they administer
    // their portfolio from the partner console, not a tenant dashboard) — DashHome already
    // has its own redirect for a partner whose licence is suspended, so this guard leaves
    // them alone rather than sending them to organisation creation.
    const isSiteAdmin = Boolean(user && hasRole(user, SITE_ADMIN_ROLES))
    if (isSiteAdmin || user?.isPartnerStaff) {
        return <>{children}</>
    }

    if (!user?.org) {
        return <CreateOrganisation />
    }

    // Only ever set for an org's own admins (agreements/services.py), so site admins and
    // partner staff, who returned above, are never stopped here.
    if (needsTermsAcceptance(user)) {
        return <AcceptTerms />
    }

    const billingRouteReachable = BILLING_ESCAPE_PATHS.some((path) =>
        location.pathname.startsWith(path)
    )
    if (activeOrg?.is_billing_active === false && !billingRouteReachable) {
        return <TrialExpired />
    }

    return <>{children}</>
}

OrgAccessGuard.propTypes = {
    children: PropTypes.node
}

export default OrgAccessGuard
